import moment from 'moment';
import numeral from 'numeral';

import { EditCustomerHandleError } from 'components/customer/EditCustomerHandleError';
import {
  ADJUST_TYPES,
  CheckStatus,
  CouponVirtualRealType,
  CustomerStatus,
  DISPLAY_SIGN_UP_CHANNEL,
  EarningRuleGlobalLimitReward,
  LAST_MODIFIED_BY,
  LanguageTransLation,
  SIGN_UP_METHOD,
  SIGN_UP_METHOD_DISPLAY,
  SavedStatus,
  StatusTag,
} from 'config/CustomEnums';
import { tierTypeConfig } from 'config/TierTypeConfig';
import { getErrorList } from 'containers/customers/customer/CustomerEditValidate';
import { apiWithResponseHandle, loading } from 'models/LoadingUtil';
import {
  activeCustomer,
  adjustCustomerRewards,
  createCustomerRemark,
  deactiveCustomer,
  deleteCustomerRemark,
  deleteCustomers,
  getAllCustomerProfileFields,
  getAllCustomers,
  getCustomerActivePoints,
  getCustomerActivityLog,
  getCustomerGroups,
  getCustomerReportsByPage,
  getCustomerSelectableSegments,
  getCustomersByPage,
  getOneCustomer,
  getOneCustomerBehavioralData,
  getPagedCustomerAdditionalDataFields,
  resumeCustomer,
  sendMessageToResetPassword,
  transferCustomer,
  updateCustomer,
} from 'services/CustomerAPIHelper';
import {
  adjustValidation,
  parseAdjustValidationResultList,
  parseAdjustValidationTitle,
} from 'services/TransactionRecordsAPIHelper';

import {
  convertCursorToNumber,
  convertNumberToCursor,
  convertPKToId,
  delay,
  enLocaleTransactionNumberFormatter,
  formatNumberWithCommas,
  getDisplayLastModifiedBy,
  getObjectFromSessionStorage,
  saveToSessionStorage,
} from 'utils';
import {
  TimeFormater,
  formatDate,
  getDetailPageDisplayLastModifiedDate,
  getListDisplayLastModifiedDate,
  getRemarkCreationDateFormatted,
} from 'utils/TimeFormatUtil';

const customerSessionKey = 'tempCustomer';
const getInitialState = () => ({
  customerList: [],
  tempCustomerList: [],
  customerGroup: [],
  customerSegments: [],
  listDisplayFields: [
    { displayName: 'ID', fieldName: 'pk', linked: true },
    {
      displayName: 'Name (preferred name)',
      fieldName: 'name',
      orderField: 'firstName',
      linked: false,
    },
    { displayName: 'Test Customer', fieldName: 'testCustomer' },
    { displayName: 'Mobile number', fieldName: 'mobileNumber' },
    { displayName: 'Email', fieldName: 'email' },
    {
      displayName: 'Membership ID',
      fieldName: 'membershipId',
      orderField: 'membershipId',
    },
    {
      displayName: 'Current Level',
      fieldName: 'level',
      orderField: 'level',
      hide: !tierTypeConfig.levels,
    },
    {
      displayName: 'Segment',
      fieldName: 'segment',
      hide: !tierTypeConfig.segments,
    },
    { displayName: 'Source', fieldName: 'displaySignUpChannel' },
    { displayName: 'Additional data', fieldName: 'displayExtraData' },
    { displayName: 'Last modified time', fieldName: 'displayLastModifiedTime' },
    { displayName: 'Status', fieldName: 'status' },
  ],
  reportListDisplayFields: [
    { displayName: 'ID', fieldName: 'pk' },
    { displayName: 'Name', fieldName: 'name', linked: false },
    { displayName: 'Report type', fieldName: 'reportType' },
    { displayName: 'Time range', fieldName: 'displayTimeRange' },
    { displayName: 'Creation time', fieldName: 'creationDate' },
  ],
  pagedCustomerList: [],
  pageInfo: {
    startCursor: '',
    endCursor: '',
    hasNextPage: false,
    hasPreviousPage: false,
  },
  listTotalCount: 0,
  currentLastCursor: '',
  currentPage: 0,
  totalPage: 0,
  totalCount: 0,
  checkedList: [],
  customer: {},
  sectionsConfig: [],
  customerBehavioralData: {},
  checked: CheckStatus.initOrNotChecked,
  errorsList: [],
  test: false,
  saved: SavedStatus.init,
  activityLogs: [],
  activePoints: [],
  activePointsPage: 0,
  activePointsHasNextPage: false,
  activePointsIsLoading: false,
  formChanged: false,
  filters: {
    groups: [],
    segments: [],
    levels: [],
    genders: [],
    age: [0, 0],
    startDate: '',
    endDate: '',
    count: 0,
  },
  hasUpdatedDefaultValues: false,
  formHasSubmitted: false,
  pagedCustomerReportList: [],
  customerAdditionalDataFields: [],
  customerProfileLoading: false,
  customerProfileBehavioralDataLoading: false,
  adjustValidationResult: {},
  primaryAccount: {},
  primaryAccountLoading: false,
});

const getLevelPrivilege = (level) => {
  let levelPrivileges = [];
  switch (level) {
    case 'Level 1':
      levelPrivileges = ['Discount for the transaction'];
      break;
    case 'Level 2':
      levelPrivileges = [
        'Discount for the transaction',
        'Refunds for no reason',
      ];
      break;
    case 'Level 3':
      levelPrivileges = [
        'Discount for the transaction',
        'Refunds for no reason',
        '7 days insured',
      ];
      break;
    case 'Level 4':
      levelPrivileges = [
        'Discount for the transaction',
        'Refunds for no reason',
        '7 days insured',
        '24h Customer service',
      ];
      break;
    default:
      levelPrivileges = [];
      break;
  }

  return levelPrivileges;
};

const getReferralSource = (item) => {
  const referrer = item.referrer
    ? `[${item.referrer.membershipId}] ${item.referrer.firstName} ${item.referrer.lastName}`
    : '';
  let referredCampaigns = item.referredByCampaigns?.map(
    (campaign) => `[ID:${campaign.pk}] ${campaign.name}`,
  );
  if (referredCampaigns?.length <= 0) {
    referredCampaigns = item.referredByCampaign?.pk
      ? [`[ID:${item.referredByCampaign.pk}] ${item.referredByCampaign.name}`]
      : [];
  }
  const referralEarningRules = item?.referralEarningRules?.edges?.map(
    (item) => ({
      ...item.node,
      promotionCode: item.node.newMemberTypePromotionCode,
      displayEarningRule: `[ID:${item.node.pk}] ${item.node.name || '-'}`,
    }),
  );
  const referralMethod = item?.referralMethod;
  return {
    referralMethod,
    referrer,
    referredCampaigns,
    promotionCode: item.promotionCode,
    referralEarningRules,
  };
};

const getDisplayStatus = (status) => {
  switch (status) {
    case CustomerStatus.active:
      return StatusTag.activated;
    case CustomerStatus.deactive:
      return StatusTag.deactivated;
    case CustomerStatus.temporaryEarning:
      return StatusTag.temporaryEarning;
    case CustomerStatus.temporary:
      return StatusTag.temporary;
    case CustomerStatus.prospect:
      return StatusTag.prospect;
    case CustomerStatus.deleteRequested:
      return StatusTag.deleteRequested;
    case CustomerStatus.deleted:
      return StatusTag.deleted;
    case CustomerStatus.merged:
      return StatusTag.merged;
    default:
      return '-';
  }
};

const isPlural = (data) => {
  return data >= 2;
};

const getDisplayJoinedTime = (creationDate) => {
  const now = new Date();
  const joinedDate = new Date(creationDate);
  const deltaMonths =
    (now.getFullYear() - joinedDate.getFullYear()) * 12 +
    now.getMonth() -
    joinedDate.getMonth();
  const year = parseInt(deltaMonths / 12);
  const month = deltaMonths % 12;

  const yearComment = year ? ` ${year} year${isPlural(year) ? 's' : ''}` : '';
  const monthComment =
    year && !month ? '' : ` ${month} month${isPlural(month) ? 's' : ''}`;
  const andComment = yearComment && monthComment ? ' and' : '';

  return `Joined${yearComment}${andComment}${monthComment} (${formatDate(
    joinedDate,
  )})`;
};

const getDisplayLevelRenewalDate = (levelRenewalDate) => {
  const now = new Date();
  const renewalDate = new Date(levelRenewalDate);

  const seconds = renewalDate.getTime() - now.getTime();
  const oneDay = 24 * 60 * 60 * 1000;
  const deltaDays = parseInt(seconds / oneDay);

  return `Level renewal date: ${deltaDays} days from today (${formatDate(
    renewalDate,
  )})`;
};

const getDisplaySignUpChannel = (signUpChannel) => {
  return DISPLAY_SIGN_UP_CHANNEL[signUpChannel] || '-';
};

const getDisplayExtraData = (userCustomData, customerAdditionalDataFields) => {
  if (!userCustomData) {
    return '-';
  }
  const displayExtraData = [];
  const dataJson = JSON.parse(userCustomData);
  for (const fieldName of customerAdditionalDataFields) {
    // field display_order
    for (const [key, value] of Object.entries(dataJson)) {
      if (fieldName === key) {
        const keyValueString = `${key}:${value}`;
        displayExtraData.push(keyValueString);
        break;
      }
    }
  }
  for (const [key, value] of Object.entries(dataJson)) {
    if (customerAdditionalDataFields.includes(key)) {
      continue;
    }
    const keyValueString = `${key}:${value}`;
    displayExtraData.push(keyValueString);
  }
  console.log(
    'customerAdditionalDataFields@displayExtraData',
    displayExtraData,
  );
  return displayExtraData.join(', ');
};

export const getCustomerDisplayName = (item, showAllName = false) => {
  if (!item) {
    return '-';
  }

  const username = [item.firstName, item.lastName].filter(Boolean).join(' ');

  if (showAllName) {
    return username.concat(item.nickname ? ` (${item.nickname})` : '');
  }
  if (item.nickname) {
    return item.nickname;
  }
  return username;
};

const parseListCustomer = (item, customerAdditionalDataFields) => {
  const displayExtraData = getDisplayExtraData(
    item?.userCustomData,
    customerAdditionalDataFields,
  );
  const displayFirstNameAndLastName = getCustomerDisplayName(item);
  const data = {
    pk: item.pk,
    ssoUid: item.ssoUid,
    owner: displayFirstNameAndLastName,
    name: item.nickname
      ? `${displayFirstNameAndLastName} (${item.nickname})`
      : displayFirstNameAndLastName,
    membershipId: item.membershipId,
    mobileNumber:
      item.mobilePhoneNumberCountryCode &&
      item.mobilePhoneNumberSubscriberNumber
        ? `+${item.mobilePhoneNumberCountryCode}\r\n${item.mobilePhoneNumberSubscriberNumber}`
        : '-',
    email: item.emailAddress,
    pointAccount: item.pointAccount,
    level: item.pointAccount?.currentLevel?.levelName,
    segment: item.segments?.edges.map((seg) => seg.node.name).join(', '),
    status: getDisplayStatus(item?.status),
    testCustomer: item.isAssignedAsTestingCustomer ? 'Test\r\ncustomer' : '-',
    userCustomData: item?.userCustomData,
    displayExtraData: displayExtraData,
    displayLastModifiedTime: getListDisplayLastModifiedDate(
      item?.lastModifiedDate,
    ),
    displaySignUpChannel: getDisplaySignUpChannel(item.signUpChannel),
    dateOfBirth: item.dateOfBirth,
  };
  return data;
};

const parseDetailCustomerBehavioralData = (item) => {
  const displayAverageTransactionValue = enLocaleTransactionNumberFormatter(
    item?.averageTransactionValue,
  );
  let displayLastPurchaseDate = '-';
  if (item?.lastPurchaseDate) {
    const purchaseDateString = formatDate(item.lastPurchaseDate, 'DD MMM yyyy');
    const timeDiff = moment().diff(moment(item.lastPurchaseDate));
    const dayDiff = Math.round(timeDiff / (1000 * 60 * 60 * 24));
    let displayDayDiff = '-';
    if (dayDiff > 365) {
      displayDayDiff = `${numeral(dayDiff / 365).format('0.0')} years ago`;
    } else if (dayDiff >= 7 && dayDiff % 7 === 0) {
      displayDayDiff = `${dayDiff / 7} weeks ago`;
    } else if (dayDiff >= 7 && dayDiff % 7 > 0) {
      displayDayDiff = `${Math.round(dayDiff / 7)} weeks ${
        dayDiff % 7
      } days ago`;
    } else if (dayDiff >= 0) {
      displayDayDiff = `${dayDiff} days ago`;
    }
    displayLastPurchaseDate = `${displayDayDiff} (${purchaseDateString})`;
  }
  const displayTotalTransactionValue = enLocaleTransactionNumberFormatter(
    item?.totalTransactionValue,
  );
  const displayCountTransaction = item?.countTransaction;
  const displayCountTransactionInPhysicalStore =
    item?.countTransactionInPhysicalStore;
  const displayCountTransactionInOnlineStore =
    item?.countTransactionInOnlineStore;

  const displayTopCategoryList = item?.topCategoryList?.map(
    (item) => `[ID:${item?.pk}] ${item?.name}`,
  );
  const displayTopSkuList = item?.topSkuList;
  const displayTopStoreList = item?.topStoreList?.map(
    (item) => `[ID:${item?.pk}] ${item?.name}`,
  );
  return {
    displayAverageTransactionValue,
    displayLastPurchaseDate,
    displayTotalTransactionValue,
    displayCountTransaction,
    displayCountTransactionInPhysicalStore,
    displayCountTransactionInOnlineStore,
    displayTopCategoryList,
    displayTopSkuList,
    displayTopStoreList,
  };
};

const parseCustomerCoupon = (item) => {
  const virtualRealType = item.node.virtualRealType;
  let groupKey = {
    pk: '1',
    name: 'Not require applicable stores',
  };
  if (virtualRealType === CouponVirtualRealType.Virtual) {
    groupKey = {
      pk: '2',
      name: 'Applicable store not yet selected',
    };
  } else if (virtualRealType === CouponVirtualRealType.Real) {
    groupKey = {
      pk: '3',
      name: 'Applicable store selected',
    };
  }
  return {
    pk: item.node.pk,
    name: `[ID: ${item.node.pk}] ${item.node.template.name}`,
    templatePK: item.node.template.pk,
    templateName: item.node.template.name,
    couponType: item.node.template.couponType,
    storeName: item.node.store?.name,
    value: item.node,
    groupKey,
  };
};

const getDisplayLastModifiedInfo = (item) => {
  let lastModifiedBy = getDisplayLastModifiedBy(item);
  const lastModifiedByAdmin = item?.lastModifiedByAdmin;

  if (
    [LAST_MODIFIED_BY.CMS_ADMIN, LAST_MODIFIED_BY.MERCHANT_ADMIN].includes(
      item.lastModifiedBy,
    ) &&
    lastModifiedByAdmin
  ) {
    lastModifiedBy = `[ID: ${lastModifiedByAdmin.pk || '-'}] ${
      lastModifiedByAdmin.username || '-'
    }`;
  }
  return `Last modified time: ${getDetailPageDisplayLastModifiedDate(
    item?.lastModifiedDate,
  )} (by ${lastModifiedBy})`;
};

const parseTransferHistory = (item) => {
  const countryCode = item?.node?.unwantedAccountMobilePhoneNumberCountryCode;
  const subscriberNumber =
    item?.node?.unwantedAccountMobilePhoneNumberSubscriberNumber;

  const mobile =
    countryCode && subscriberNumber
      ? `+${countryCode} ${subscriberNumber}`
      : '';
  const email = item?.node?.unwantedAccountEmailAddress;

  return {
    creationDate: formatDate(item?.node?.creationDate),
    membershipId: item?.node?.unwantedAccount?.membershipId,
    mobile,
    email,
  };
};

const parseDetailCustomer = (item) => {
  if (!item) {
    return {};
  };
  const allDisplayRemarks = item.remarks?.edges.map((seg) => {
    const remark = seg.node.remark;
    const creationDate = seg.node.creationDate;
    const displayCreationDate = getRemarkCreationDateFormatted(creationDate);
    const admin = seg.node.creator;
    const adminName = seg.node.actionBySystem
      ? 'System remarks'
      : admin?.firstName
      ? `${admin.firstName} ${admin.lastName}`
      : admin?.username;
    return {
      pk: seg.node.pk,
      isCreator: seg.node.isCreator,
      adminName,
      isSystem: seg.node.actionBySystem,
      displayCreationDate,
      remark,
    };
  });
  const displayRemarks =
    allDisplayRemarks.length > 5
      ? allDisplayRemarks.slice(0, 5)
      : allDisplayRemarks;

  const customerTags = item.tags;
  const mobileNumber =
    item.mobilePhoneNumberCountryCode && item.mobilePhoneNumberSubscriberNumber
      ? `+${item.mobilePhoneNumberCountryCode}\r\n${item.mobilePhoneNumberSubscriberNumber}`
      : null;
  let displayLoginMethod = [];
  if (
    item.signUpMethod === SIGN_UP_METHOD.MOBILE_PHONE_NUMBER_AND_EMAIL_ADDRESS
  ) {
    displayLoginMethod = [item.emailAddress, mobileNumber];
  } else if (item.signUpMethod === SIGN_UP_METHOD.MOBILE_PHONE_NUMBER) {
    displayLoginMethod = [mobileNumber];
  } else if (item.signUpMethod === SIGN_UP_METHOD.EMAIL_ADDRESS) {
    displayLoginMethod = [item.emailAddress];
  }
  displayLoginMethod = displayLoginMethod.filter((item) => Boolean(item));
  const displayFirstNameAndLastName = getCustomerDisplayName(item);

  const transferHistory = item?.primaryAccountTransferredHistory?.edges?.map(
    (historyItem) => parseTransferHistory(historyItem),
  );
  const haveTransferHistory = transferHistory && transferHistory?.length;

  return {
    ...item,
    owner: displayFirstNameAndLastName,
    name: item.nickname
      ? `${displayFirstNameAndLastName} (${item.nickname})`
      : displayFirstNameAndLastName,
    membershipId: item.membershipId,
    signUpMethod: item.signUpMethod,
    signUpMethodDisplay: SIGN_UP_METHOD_DISPLAY?.[item.signUpMethod] || '-',
    shopifyLinked: item?.shopifyLinked ?? false,
    chineseFirstName: '-',
    chineseLastName: '-',
    chineseName: '-',
    mobileNumber: mobileNumber || '-',
    email: item.emailAddress,
    displayLoginMethod: displayLoginMethod || [],
    level: item.pointAccount?.currentLevel?.levelName,
    displayJoinedTime: getDisplayJoinedTime(item.creationDate),
    displayLevelRenewalDate: getDisplayLevelRenewalDate(
      item.pointAccount?.currentLevelRenewDatetime,
    ),
    displayLastModified: getDisplayLastModifiedInfo(item),
    levelRenewalDate: formatDate(item.pointAccount?.currentLevelRenewDatetime),
    currentLevelRenewDatetime: item.pointAccount?.currentLevelRenewDatetime,
    levelExpiredIndays: item.pointAccount?.currentLevelExpiredInXDays,
    displayRemarks,
    allDisplayRemarks,
    displaySegmentList: item.segments?.edges.map((seg) => seg.node.name),
    segment: item.segments?.edges.map((seg) => seg.node.name).join(', '),
    segmentsIn: item.segments?.edges
      .map((segment) => segment.node.pk)
      .join(','),
    status: getDisplayStatus(item?.status),
    customerStatus: item?.status,
    displayStatus: getDisplayStatus(item?.status),
    displayTagsList: customerTags.map((tag) => tag.name),
    displayInTags: customerTags.map((tag) => ({
      pk: tag.id,
      name: tag.name,
      value: tag,
    })),

    // group: groups.map((group) => group.name).join(', '),
    // groupsIn: groups.map((group) => group.pk).join(','),
    // inGroups: groups.map((group) => ({
    //   pk: group.pk,
    //   name: group.name,
    //   value: group,
    // })),
    // displayInGroups: displayGroups.map((group) => ({
    //   pk: group.pk,
    //   name: group.name,
    //   value: group,
    // })),
    referrerUser: item.referrer
      ? {
          label: `[${item.referrer.membershipId}] ${item.referrer.firstName} ${item.referrer.lastName}`,
          value: item.referrer,
        }
      : null,
    referrerCampaign: item.referredByCampaign
      ? {
          label: item.referredByCampaign?.name,
          value: item.referredByCampaign,
        }
      : null,
    referralEarningRules: item?.referralEarningRules?.edges?.map((item) => ({
      label: `[ID:${item.node.pk}] ${item.node.name || '-'}`,
      value: item.node,
    })),
    referralSource: getReferralSource(item),
    formateCreated: formatDate(item.creationDate),
    lifetime: item.totalSpending,
    averageOrder: item.averageOrderValue,
    totalOrders: item.totalNumberOfOrders,
    lastOrderDays: item.daysFromLastOrder,
    acceptDirectMarketing: item.hasAgreedDirectMarketing ? 'Yes' : 'No',
    acceptDirectMarketingViaEmail: item.directMarketingViaEmail ? 'Yes' : 'No',
    legalAgreement: 'Yes',
    dataProcessingAgreement: 'Yes',
    pointsBalance: item.pointAccount?.balance,
    pointsBalanceWithComma: formatNumberWithCommas(
      item.pointAccount?.balance || '',
    ),
    totalLifetimePointsUsed: item.pointAccount?.totalPointsUsed,
    totalLifetimePointsExpired: item.pointAccount?.totalPointsExpired,
    tpe: item.pointAccount?.totalPointsEarned,
    levelPrivilege: getLevelPrivilege(
      item.pointAccount?.currentLevel?.levelName,
    ).join(', '),
    pointsToUpgrade: item.pointAccount?.pointsToUpgrade,
    pointsToUpgradeWithComma: formatNumberWithCommas(
      item.pointAccount?.pointsToUpgrade || '',
    ),
    pointsToRetain: item.pointAccount?.pointsToRetain,
    pointsToRetainWithComma: formatNumberWithCommas(
      item.pointAccount?.pointsToRetain || '',
    ),
    availableCampaign: '-',
    coupons: item.coupons?.edges.map((item, index) => item.node.template.name),
    ownedCoupons: item.coupons?.edges.map((item, index) =>
      parseCustomerCoupon(item),
    ),
    preferredMessageLanguageDisplay: item.preferredMessageLanguage
      ? LanguageTransLation[item.preferredMessageLanguage]
      : '-',
    socialMedia: '-',
    gender: item.gender?.value,
    displayGender: item.gender?.label,
    testCustomer: item.isAssignedAsTestingCustomer ? 'Test\r\ncustomer' : '-',
    assignToTest: item.isAssignedAsTestingCustomer ? 'Yes' : 'No',
    displayDateOfBirth: formatDate(item.dateOfBirth, 'MMM yyyy'),
    userCustomData: item?.userCustomData ? JSON.parse(item.userCustomData) : {},
    signUpByMerchant: item.signUpByMerchant?.pk
      ? `[${item.signUpByMerchant?.pk}] ${item.signUpByMerchant?.username}`
      : '-',
    displaySignUpChannel: getDisplaySignUpChannel(item.signUpChannel),
    transferHistory,
    haveTransferHistory,
  };
};

const parseCustomerList = (data, fieldsData) => {
  const customerAdditionalDataFields =
    fieldsData?.map((edge) => edge.node.fieldName).filter((item) => !!item) ||
    [];
  return data.map((item) => {
    if (!item.node.isReset) {
      return parseListCustomer(item.node, customerAdditionalDataFields);
    }
    return undefined;
  });
};
function parseCustomerReportType(reportType) {
  switch (reportType) {
    case 'WEEKLY':
      return 'Weekly';
    case 'MONTHLY':
      return 'Monthly';
    default:
      break;
  }
}

function parseCustomerReport(item) {
  return {
    pk: item.pk,
    id: item.id,
    name: item.reportName,
    reportType: parseCustomerReportType(item.reportType),
    displayTimeRange: `${formatDate(
      item.startTime,
      'DD MMM yyyy',
    )} - ${formatDate(item.endTime, 'DD MMM yyyy')}`,
    creationDate: formatDate(item.creationDate, 'DD MMM yyyy'),
    reportFileUrl: item.reportFileUrl,
  };
}

const parseActivityLogs = (data) => {
  console.log('@@152: ', data);
  return data?.map((item) => {
    return {
      ...item.node,
      text: item.node.customerPointAndLevelLogTitle,
      updateTime: formatDate(
        item.node.creationDate,
        TimeFormater.dayMonthYearWeekTime,
      ),
    };
  });
};

const parseActivePoints = (data) => {
  return data?.map((item) => {
    const { earnedValue, expirationDate } = item.node;
    const expiry = expirationDate;
    return {
      points: earnedValue,
      expiry,
    };
  });
};

const parseSegment = (data) => {
  return {
    pk: data?.pk,
    name: data?.name,
    value: data,
  };
};

const parseCustomerSelectableSegments = (data) => {
  return data?.map((item) => parseSegment(item));
};

const parseChoiceData = (item) => {
  const data = {
    label: item.displayLabel || item.fieldValue,
    value: item.fieldValue,
  };
  console.log('assembleConfig@choice', data);
  return data;
};

const parseFieldData = (item) => {
  const data = {
    displayPriority: item.displayPriority,
    displayTitle: item.displayTitle,
    displayTips: item?.tips,
    editable: item.customerEditable,
    mandatory: item.isMandatory,
    fieldName: item.fieldName,
    fieldType: item.fieldType,
    choices: item?.choices?.edges?.map((edge) => parseChoiceData(edge?.node)),
  };
  console.log('assembleConfig@field', data);
  return data;
};

const parseSectionData = (item) => {
  const data = {
    name: item.name,
    displayPriority: item?.displayPriority,
    fields: item?.fields?.edges?.map((edge) => parseFieldData(edge?.node)),
  };
  console.log('assembleConfig@section', data);
  return data;
};

export default {
  namespace: 'customerList',
  state: getInitialState(),
  reducers: {
    updateState(state, { payload }) {
      return { ...state, ...payload };
    },

    updateAllCustomerTempList(state, { payload }) {
      const { tempCustomerList, page } = payload;
      return {
        ...state,
        tempCustomerList:
          page > 1
            ? [...state.tempCustomerList, ...tempCustomerList]
            : tempCustomerList,
        totalCount:
          page > 1
            ? state.tempCustomerList.length + tempCustomerList.length
            : tempCustomerList.length,
      };
    },

    updateCustomerList(state, { payload }) {
      const { customerList, page } = payload;
      return {
        ...state,
        customerList:
          page > 1 ? [...state.customerList, ...customerList] : customerList,
        totalCount:
          page > 1
            ? state.customerList.length + customerList.length
            : customerList.length,
      };
    },

    updateCustomerTags(state, { payload }) {
      const { segments, page } = payload;
      return {
        ...state,
        customerSegments:
          page > 1 ? [...state.customerSegments, ...segments] : segments,
      };
    },

    assembleRefreshCustomerActivePoints(state, { payload }) {
      const { data } = payload;
      const activePoints = parseActivePoints(data?.customerPoints.edges);
      const { totalCount } = data?.customerPoints;
      const { hasNextPage } = data?.customerPoints?.pageInfo;
      return {
        ...state,
        activePoints,
        activePointsPage: 1,
        activePointsHasNextPage: hasNextPage,
        activePointsIsLoading: false,
      };
    },

    assembleLoadMoreCustomerActivePoints(state, { payload }) {
      const { data, page } = payload;
      const activePoints = parseActivePoints(data?.customerPoints.edges);
      const { hasNextPage } = data?.customerPoints?.pageInfo;
      return {
        ...state,
        activePoints: [...state.activePoints, ...activePoints],
        activePointsPage: page,
        activePointsHasNextPage: hasNextPage,
        activePointsIsLoading: false,
      };
    },

    assembleCustomerAdditionalDataFields(state, { payload }) {
      const customerAdditionalDataFields =
        payload?.list
          ?.map((edge) => edge.node.fieldName)
          .filter((item) => !!item) || [];
      return {
        ...state,
        customerAdditionalDataFields,
      };
    },

    assembleCustomerBehavioralData(state, { payload }) {
      const customerBehavioralData = parseDetailCustomerBehavioralData(
        payload?.customer,
      );
      return {
        ...state,
        customerBehavioralData,
      };
    },

    changeVals(state, { payload }) {
      // deprecated
      console.log('@@customer edit: vals changed', payload);
      let tempCustomer = getObjectFromSessionStorage(customerSessionKey);

      let data = {};
      if (payload.vals) {
        data = payload.vals;
      }

      tempCustomer = { ...tempCustomer, ...data };
      saveToSessionStorage(customerSessionKey, tempCustomer);

      return {
        ...state,
        formChanged: true,
      };
    },

    clearData(state, { payload }) {
      return {
        ...state,
        ...getInitialState(),
      };
    },

    updateAllCustomerAdditionalDataFields(state, { payload }) {
      const { page, additionalDataFieldNames, additionalDataFieldValues } =
        payload;
      return {
        ...state,
        customerAdditionalDataFields:
          page > 1
            ? [
                ...state.customerAdditionalDataFields,
                ...additionalDataFieldNames,
              ]
            : additionalDataFieldNames,
        additionalDataFieldValues:
          page > 1
            ? [...state.additionalDataFieldValues, ...additionalDataFieldValues]
            : additionalDataFieldValues,
      };
    },
  },

  effects: {
    // getAllCustomerGroups: [
    //   function* ({ payload }, { call, select, put }) {
    //     const afterCursor = payload?.afterCursor;
    //     const pageItemCount = 100;
    //     const serviceArgs = [
    //       getCustomerGroups,
    //       {
    //         pageItemCount,
    //         afterCursor,
    //         moreSearch: {
    //           showInCustomerProfile: true,
    //         },
    //       },
    //     ];
    //     function* onSuccess(data) {
    //       const customerGroups = data.customerGroups;
    //       const groups = parseCustomerGroups(customerGroups?.edges);

    //       const hasNextPage = groups?.length === pageItemCount;
    //       const endCursor = customerGroups?.pageInfo?.endCursor;

    //       const page = payload.page ? payload.page + 1 : 1;

    //       yield put({
    //         type: 'updateCustomerGroup',
    //         payload: {
    //           customerGroups: groups,
    //           page,
    //         },
    //       });

    //       if (hasNextPage) {
    //         yield put({
    //           type: 'getAllCustomerGroups',
    //           payload: {
    //             afterCursor: endCursor,
    //             page,
    //           },
    //         });
    //       }
    //     }
    //     yield apiWithResponseHandle(serviceArgs, onSuccess);
    //   },
    //   { type: 'takeLatest' },
    // ],
    getCustomerSelectableSegments: [
      function* ({ payload }, { call, select, put }) {
        const afterCursor = payload?.afterCursor;
        const pageItemCount = 100;
        const serviceArgs = [
          getCustomerSelectableSegments,
          {
            pageItemCount,
            afterCursor,
          },
        ];
        function* onSuccess(data) {
          const segments = data.segments.edges?.map((item) => item.node);
          const parsedSegments = parseCustomerSelectableSegments(segments);

          const hasNextPage = data?.segments?.pageInfo?.hasNextPage;
          const endCursor = data?.segments?.pageInfo?.endCursor;

          const page = payload?.page ? payload.page + 1 : 1;

          yield put({
            type: 'updateCustomerTags',
            payload: {
              segments: parsedSegments,
              page,
            },
          });

          if (hasNextPage) {
            yield put({
              type: 'getCustomerSelectableSegments',
              payload: {
                afterCursor: endCursor,
                page,
              },
            });
          }
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    getAllCustomers: [
      function* ({ payload }, { call, put, select }) {
        const { afterCursor, search } = payload;
        const page = afterCursor ? payload.page : 1;
        const response = yield call(getAllCustomers, afterCursor, search);
        if (!response || response.status >= 300) {
          return;
        }
        if (response.data.data?.customers) {
          const customerInfo = response.data.data.customers;
          const tempCustomerList = parseCustomerList(customerInfo.edges);
          yield put({
            type: 'updateAllCustomerTempList',
            payload: {
              tempCustomerList: tempCustomerList,
              page: page,
            },
          });
          if (customerInfo.pageInfo.hasNextPage) {
            yield put({
              type: 'getAllCustomers',
              payload: {
                afterCursor: customerInfo.pageInfo.endCursor,
                page: page + 1,
                search: search,
              },
            });
          } else {
            const tempCustomerList = yield select(
              (state) => state.customerList.tempCustomerList,
            );
            yield put({
              type: 'updateState',
              payload: {
                customerList: tempCustomerList,
              },
            });
          }
        }
      },
      { type: 'takeLatest' },
    ],
    getPagedCustomers: [
      function* ({ payload }, { call, put, all }) {
        const page = payload.page;
        const pageCursor = page
          ? convertNumberToCursor((page - 1) * 20 - 1)
          : '';
        const serviceArgs = [
          getCustomersByPage,
          pageCursor,
          payload.reverse,
          payload.search,
          payload.segments,
          payload.levels,
          payload.age,
          payload.gender,
          payload.status,
          payload.birthday_start_date,
          payload.birthday_end_date,
          payload.birth_month,
          payload.sort,
          payload,
        ];
        console.log('@@363');
        function* onSuccess(data) {
          const pageInfo = data?.customers?.pageInfo;

          const currentLastCursor = pageInfo?.endCursor;
          const totalCount = data?.customers?.totalCount;
          console.log('@@115: ', data);
          const pagedCustomerList = parseCustomerList(
            data?.customers?.edges,
            data?.customerAdditionalDataFields?.edges,
          );
          if (payload.isSelectorLoad) {
            yield all([
              put({
                type: 'updateCustomerList',
                payload: {
                  customerList: pagedCustomerList,
                  page,
                },
              }),
            ]);
          } else {
            yield all([
              put({
                type: 'updateState',
                payload: {
                  pagedCustomerList: pagedCustomerList,
                  pageInfo: {
                    startCursor:
                      convertCursorToNumber(pageInfo?.startCursor) + 1,
                    endCursor: convertCursorToNumber(pageInfo?.endCursor) + 1,
                  },
                  currentLastCursor: currentLastCursor,
                  listTotalCount: totalCount,
                  totalPage: Math.ceil(totalCount / 20),
                },
              }),
              put({
                type: 'assembleCustomerAdditionalDataFields',
                payload: { list: data.customerAdditionalDataFields.edges },
              }),
              put({
                type: 'updateCustomerList',
                payload: {
                  customerList: pagedCustomerList,
                  page,
                },
              }),
            ]);
          }
        }
        // eslint-disable-next-line require-yield
        function* onFailed(data) {
          console.log('@@122: ', data);
        }

        if (payload?.isSelectorLoad) {
          yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed);
        } else {
          yield loading(serviceArgs, onSuccess, onFailed);
        }
      },
      { type: 'takeLatest' },
    ],
    getPagedCustomersReports: [
      function* ({ payload }, { call, put, all }) {
        const page = payload.page;
        const pageCursor = page
          ? convertNumberToCursor((page - 1) * 20 - 1)
          : '';
        const serviceArgs = [getCustomerReportsByPage, pageCursor, payload];
        function* onSuccess(data) {
          const customerReportData = data.customerReports.edges;
          const pageInfo = data?.customerReports?.pageInfo;
          const currentLastCursor = pageInfo?.endCursor;
          const totalCount = data?.customerReports?.totalCount;
          const customerReportList = customerReportData.map((item) =>
            parseCustomerReport(item.node),
          );
          yield all([
            put({
              type: 'updateState',
              payload: {
                pagedCustomerReportList: customerReportList,
                pageInfo: {
                  startCursor: convertCursorToNumber(pageInfo?.startCursor) + 1,
                  endCursor: convertCursorToNumber(pageInfo?.endCursor) + 1,
                },
                currentLastCursor: currentLastCursor,
                listTotalCount: totalCount,
                totalPage: Math.ceil(totalCount / 20),
              },
            }),
          ]);
        }
        yield loading(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    delete: [
      function* ({ payload }, { all, put, select }) {
        const { checkedList } = yield select((state) => ({
          checkedList: state.customerList.checkedList,
        }));

        let pks = [];
        checkedList.forEach((item) => {
          pks.push(item.pk);
        });

        const serviceArgs = [deleteCustomers, pks];
        const afterAction = payload.afterAction || (() => {});
        console.log('@@231: ', pks);
        function* onSuccess(data) {
          console.log('@@115: ', data);

          yield all([
            put({
              type: 'updateState',
              payload: {
                checkedList: [],
                formChanged: false,
              },
            }),
          ]);
          yield delay(1000);
          afterAction();
        }
        function* onFailed(data) {
          console.log('@@122: ', data);

          yield put({
            type: 'updateState',
            payload: {
              checkedList: [],
              formChanged: false,
            },
          });
        }

        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed);
      },
      { type: 'takeLatest' },
    ],
    updateCustomerPoint: [
      function* ({ payload }, { all, put, select }) {
        const tempCustomer = payload.data;
        const data = {
          id: tempCustomer.pk,
          pointsChange: {
            type: tempCustomer.pointTransaction?.value || null,
            value: tempCustomer.transactionPoint || null,
            remarks: tempCustomer.pointTransactionRemark || null,
            transactionId: tempCustomer?.transaction?.value?.pk || null,
          },
          tpeChange: {
            type: tempCustomer.tpeTransaction?.value || null,
            value: tempCustomer.transactionTpe || null,
          },
        };

        const serviceArgs = [updateCustomer, data];

        function* onSuccess(data) {
          console.log('onSuccess@@115: ', data);

          yield put({
            type: 'updateState',
            payload: {
              formChanged: false,
              formHasSubmitted: true,
              saved: SavedStatus.savedWithSuccess,
            },
          });
          if (payload?.refreshCustomer) {
            yield put({
              type: 'getOneCustomer',
              payload: {
                id: tempCustomer.pk,
              },
            });
          }
        }
        function* onFailed(data) {
          console.log('onFailed@@122: ', data);

          const errorField = data?.data?.updateCustomer?.errors?.[0]?.field;
          yield put({
            type: 'updateState',
            payload: {
              formChanged: false,
              formHasSubmitted: true,
              saved: SavedStatus.savedWithFail,
            },
          });
        }

        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed, onFailed);
      },
      { type: 'takeLatest' },
    ],
    checkCustomerProfileValid: [
      function* ({ payload }, { all, put, select }) {
        const tempCustomer = payload.data;
        const originCustomer = payload.customer;

        const errorsList = getErrorList({
          firstName: tempCustomer.firstName,
          lastName: tempCustomer.lastName,
          pointTransaction: tempCustomer.pointTransaction,
          transactionPoint: tempCustomer.transactionPoint,
          tpeTransaction: tempCustomer.tpeTransaction,
          transactionTpe: tempCustomer.transactionTpe,
          signUpMethod: tempCustomer.signUpMethod,
          mobilePhoneNumberCountryCode:
            tempCustomer.mobilePhoneNumberCountryCode,
          mobilePhoneNumberSubscriberNumber:
            tempCustomer.mobilePhoneNumberSubscriberNumber,
          // forceLevelRenewalDate: tempCustomer.forceLevelRenewalDate,
          emailAddress: tempCustomer.emailAddress,
        });

        yield put({
          type: 'updateState',
          payload: {
            errorsList,
          },
        });

        if (errorsList.length <= 0) {
          yield put({
            type: 'updateCustomer',
            payload,
          });
        }
      },
      { type: 'takeLatest' },
    ],
    updateCustomer: [
      function* ({ payload }, { all, put, select }) {
        const tempCustomer = payload.data;
        const data = {
          id: tempCustomer.pk,
          firstName: tempCustomer.firstName,
          lastName: tempCustomer.lastName,
          nickname: tempCustomer.nickname || null,
          gender: tempCustomer.gender,
          dateOfBirth: tempCustomer.dateOfBirth,
          address: tempCustomer.address,
          company: tempCustomer.company,
          signUpChannel: tempCustomer.signUpChannel,
          referrer: tempCustomer.referrerUser?.value?.pk || null,
          referralEarningRules:
            tempCustomer.referralEarningRules?.map((item) => item?.value?.pk) ||
            [],
          couponsToReclaim: tempCustomer.removedCoupons?.map(
            (item) => item.value.pk,
          ),
          groupsToJoin: tempCustomer.addTags?.map((item) => item?.pk),
          groupsToLeave: tempCustomer.removeTags?.map((item) => item?.pk),
          isAssignedAsTestingCustomer: tempCustomer.isAssignedAsTestingCustomer,
          isForcedInactive: tempCustomer.isForcedInactive,
          forceLevel: tempCustomer.forceLevel,
          forceLevelRenewalDate: tempCustomer.forceLevelRenewalDate,
          forceLevelNotifyCustomer: tempCustomer.forceLevelNotifyCustomer,
          mobilePhoneNumberCountryCode:
            tempCustomer.mobilePhoneNumberCountryCode || null,
          mobilePhoneNumberSubscriberNumber:
            tempCustomer.mobilePhoneNumberSubscriberNumber || null,
          emailAddress: tempCustomer?.emailAddress || null,
        };

        const serviceArgs = [updateCustomer, data];

        console.log('@@382: ', data);

        function* onSuccess(data) {
          console.log('@@115: ', data);

          yield put({
            type: 'updateState',
            payload: {
              formChanged: false,
              formHasSubmitted: true,
              saved: SavedStatus.savedWithSuccess,
            },
          });
          if (payload?.refreshCustomer) {
            yield put({
              type: 'getOneCustomer',
              payload: {
                id: tempCustomer.pk,
              },
            });
          }
        }
        function* onFailed(data) {
          console.log('@@122: ', data);

          const errorField = data?.data?.updateCustomer?.errors?.[0]?.field;

          if (
            errorField === 'admin_edit_mobile' ||
            errorField === 'admin_edit_email'
          ) {
            const errorsList = [
              {
                name:
                  errorField === 'admin_edit_mobile'
                    ? EditCustomerHandleError.adminEditMobile.name
                    : EditCustomerHandleError.adminEditEmail.name,
                message: data?.data?.updateCustomer?.errors?.[0]?.messages?.[0],
              },
            ];

            yield put({
              type: 'updateState',
              payload: {
                errorsList,
              },
            });
            return;
          }

          yield put({
            type: 'updateState',
            payload: {
              formChanged: false,
              formHasSubmitted: true,
              saved: SavedStatus.savedWithFail,
            },
          });
        }

        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed, onFailed);
      },
      { type: 'takeLatest' },
    ],
    updateCustomerResume: [
      function* ({ payload }, { all, put, select }) {
        const afterAction = payload.afterAction || (() => {});
        const serviceArgs = [resumeCustomer, { id: payload.data.id }];

        function* onSuccess(data) {
          console.log('@@115: ', data);
          yield afterAction();
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    updateCustomerActive: [
      function* ({ payload }, { all, put, select }) {
        let activeAPI = deactiveCustomer;
        if (payload.data.isForcedInactive) {
          activeAPI = activeCustomer;
        }
        const afterAction = payload.afterAction || (() => {});
        const serviceArgs = [activeAPI, { id: payload.data.id }];

        function* onSuccess(data) {
          console.log('@@115: ', data);

          yield afterAction();
        }
        // eslint-disable-next-line require-yield
        function* onFailed(data) {
          console.log('@@122: ', data);
        }

        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed);
      },
      { type: 'takeLatest' },
    ],
    updateCustomerAdditionalData: [
      function* ({ payload }, { all, put, select }) {
        const { data, id, successCallback, failureCallback } = payload;
        const userCustomData = JSON.stringify(data);
        console.log('userCustomData', userCustomData);
        const input = {
          id,
          userCustomData,
        };

        const serviceArgs = [updateCustomer, input];

        function* onSuccess(data) {
          console.log('updateCustomer@onSuccess: ', data);
          if (successCallback) {
            yield successCallback();
          }
        }
        function* onFailed(data) {
          console.log('updateCustomer@onFailed: ', data);
          if (failureCallback) {
            yield failureCallback();
          }
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed, onFailed);
      },
      { type: 'takeLatest' },
    ],
    adjustCustomerValidation: [
      function* ({ payload }, { all, put, select }) {
        const {
          customerId,
          isAdd = false,
          adjustType,
          value,
          validationSuccess = () => {},
          validationFailure = () => {},
        } = payload;
        if (!isAdd) {
          yield validationSuccess();
          return;
        }
        if (!value || Number(value) <= 0) {
          yield validationSuccess();
          return;
        }
        let rewardType = '';
        let unit = '';
        switch (adjustType) {
          case ADJUST_TYPES.coupon:
            rewardType = EarningRuleGlobalLimitReward.COUPONS;
            unit = 'coupons';
            break;
          case ADJUST_TYPES.stamp:
            rewardType = EarningRuleGlobalLimitReward.STAMPS;
            unit = 'stamps';
            break;
          default:
            return;
        }
        const serviceArgs = [adjustValidation, customerId, rewardType, value];

        function* onSuccess(data) {
          console.log('onSuccess', data);
          const adjustValidation = parseAdjustValidationResultList(data, unit);
          if (adjustValidation?.length > 0) {
            yield put({
              type: 'updateState',
              payload: {
                adjustValidationResult: {
                  title: parseAdjustValidationTitle(value, unit),
                  results: adjustValidation,
                },
              },
            });
            yield validationFailure();
          } else {
            yield validationSuccess();
          }
        }
        function* onFailed(data) {
          yield validationSuccess();
        }

        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed, onFailed);
      },
      { type: 'takeLatest' },
    ],
    adjustCustomerRewards: [
      function* ({ payload }, { all, put, select }) {
        const { data, successCallback, failureCallback } = payload;
        console.log('adjustCustomerRewardsData', data);

        const serviceArgs = [adjustCustomerRewards, data];

        function* onSuccess(data) {
          console.log('adjustCustomerRewards@onSuccess: ', data);
          if (successCallback) {
            yield successCallback();
          }
        }
        function* onFailed(data) {
          console.log('adjustCustomerRewards@onFailed: ', data);
          if (failureCallback) {
            yield failureCallback(
              data?.data?.adjustCustomerRewards?.errors?.[0]?.messages?.[0],
            );
          }
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed, onFailed);
      },
      { type: 'takeLatest' },
    ],
    getOneCustomerBehavioralData: [
      function* ({ payload }, { call, put, all }) {
        const serviceArgs = [
          getOneCustomerBehavioralData,
          convertPKToId('CustomerNode', payload.id),
        ];
        function* onSuccess(data) {
          console.log('getOneCustomerBehavioralData@@115: ', data);

          yield put({
            type: 'updateState',
            payload: {
              customerProfileBehavioralDataLoading: false,
            },
          });

          yield put({
            type: 'assembleCustomerBehavioralData',
            payload: {
              customer: data?.customer,
            },
          });
        }
        // eslint-disable-next-line require-yield
        function* onFailed(data) {
          console.log('@@122: ', data);

          yield put({
            type: 'updateState',
            payload: {
              customerProfileBehavioralDataLoading: false,
            },
          });
        }

        yield put({
          type: 'updateState',
          payload: {
            customerProfileBehavioralDataLoading: true,
          },
        });

        yield loading(serviceArgs, onSuccess, onFailed);
      },
      { type: 'takeLatest' },
    ],
    getOneCustomer: [
      function* ({ payload }, { call, put, all }) {
        const serviceArgs = [
          getOneCustomer,
          convertPKToId('CustomerNode', payload.id),
        ];
        function* onSuccess(data) {
          console.log('getOneCustomer@@115: ', data);
          const customer = parseDetailCustomer(data?.customer);
          const sectionsConfig =
            data?.customerAdditionalDataSections?.edges?.map((edge) =>
              parseSectionData(edge?.node),
            );
          yield all([
            put({
              type: 'updateState',
              payload: {
                customer,
                sectionsConfig,
                hasUpdatedDefaultValues: true,
              },
            }),
            put({
              type: 'assembleCustomerAdditionalDataFields',
              payload: { list: data?.customerAdditionalDataFields?.edges },
            }),
          ]);
          saveToSessionStorage(customerSessionKey, customer);
        }
        // eslint-disable-next-line require-yield
        function* onFailed(data) {
          console.log('@@122: ', data);
        }

        yield put({
          type: 'updateState',
          payload: {
            customerProfileLoading: true,
          },
        });

        yield loading(serviceArgs, onSuccess, onFailed);

        yield put({
          type: 'updateState',
          payload: {
            customerProfileLoading: false,
          },
        });
      },
      { type: 'takeLatest' },
    ],
    getPrimaryAccount: [
      function* ({ payload }, { call, put, all }) {
        const serviceArgs = [
          getOneCustomer,
          convertPKToId('CustomerNode', payload.id),
        ];
        function* onSuccess(data) {
          const customer = parseDetailCustomer(data?.customer);
          yield all([
            put({
              type: 'updateState',
              payload: {
                primaryAccount: customer,
              },
            }),
          ]);
        }

        yield put({
          type: 'updateState',
          payload: {
            primaryAccountLoading: true,
          },
        });

        yield loading(serviceArgs, onSuccess);

        yield put({
          type: 'updateState',
          payload: {
            primaryAccountLoading: false,
          },
        });
      },
      { type: 'takeLatest' },
    ],
    getCustomerActivityLog: [
      function* ({ payload }, { call, put }) {
        const serviceArgs = [
          getCustomerActivityLog,
          {
            ssoUid: payload.ssoUid,
            customerId: payload.customerId,
          },
        ];
        function* onSuccess(data) {
          console.log('@@115: ', data);
          const activityLogs = parseActivityLogs(
            data?.customerActivityLogs.edges,
          );
          yield put({
            type: 'updateState',
            payload: {
              activityLogs,
            },
          });
        }
        // eslint-disable-next-line require-yield
        function* onFailed(data) {
          console.log('@@122: ', data);
        }

        yield loading(serviceArgs, onSuccess, onFailed);
      },
      { type: 'takeLatest' },
    ],
    refreshCustomerActivePointList: [
      function* ({ payload }, { call, put }) {
        const { customerId } = payload;
        const page = 1;
        const serviceArgs = [
          getCustomerActivePoints,
          {
            customerId,
            page,
          },
        ];
        function* onSuccess(data) {
          console.log('getCustomerActivePoints@1385: ', data);
          yield put({
            type: 'assembleRefreshCustomerActivePoints',
            payload: {
              data,
            },
          });
        }
        function* onFailed(data) {
          yield console.log('getCustomerActivePoints@1401: ', data);
        }
        yield loading(serviceArgs, onSuccess, onFailed);
      },
      { type: 'takeLatest' },
    ],
    loadMoreCustomerActivePointList: [
      function* ({ payload }, { call, select, put }) {
        const activePointsIsLoading = yield select(
          (state) => state.customerList.activePointsIsLoading,
        );
        if (activePointsIsLoading) {
          console.log('getCustomerActivePoints activePointsIsLoading');
          return;
        }
        const activePointsHasNextPage = yield select(
          (state) => state.customerList.activePointsHasNextPage,
        );
        if (!activePointsHasNextPage) {
          console.log('getCustomerActivePoints activePointsHasNextPage');
          return;
        }
        const { customerId } = payload;
        const nowPage = yield select(
          (state) => state.customerList.activePointsPage,
        );
        const page = Number(nowPage) + 1;

        console.log('getCustomerActivePoints page', page);
        yield put({
          type: 'updateState',
          payload: {
            activePointsIsLoading: true,
          },
        });
        const serviceArgs = [
          getCustomerActivePoints,
          {
            customerId,
            page,
          },
        ];
        function* onSuccess(data) {
          console.log('getCustomerActivePoints@1455: ', data);
          yield put({
            type: 'assembleLoadMoreCustomerActivePoints',
            payload: {
              data,
              page,
            },
          });
        }
        function* onFailed(data) {
          console.log('getCustomerActivePoints@1465: ', data);
          yield put({
            type: 'updateState',
            payload: {
              activePointsIsLoading: false,
            },
          });
        }
        yield loading(serviceArgs, onSuccess, onFailed);
      },
      { type: 'takeEvery' },
    ],
    createCustomerRemark: [
      function* ({ payload }, { call, put, all }) {
        const serviceArgs = [
          createCustomerRemark,
          {
            customer: payload.customer,
            remark: payload.remark,
          },
        ];
        function* onSuccess(data) {
          yield put({
            type: 'getOneCustomer',
            payload: {
              id: payload.customer,
            },
          });
        }
        // eslint-disable-next-line require-yield
        function* onFailed(data) {
          console.log('@@create customer remark failed: ', data);
        }

        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed, onFailed);
      },
      { type: 'takeLatest' },
    ],
    deleteCustomerRemark: [
      function* ({ payload }, { select, put }) {
        const serviceArgs = [deleteCustomerRemark, payload.id];
        function* onSuccess() {
          yield put({
            type: 'getOneCustomer',
            payload: {
              id: payload.customer,
            },
          });
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    getPagedCustomerAdditionalDataFields: [
      function* ({ payload }, { select, put }) {
        const {
          actionAfterSuccess,
          search,
          page,
          pageSize: customPageSize,
          queryDetail,
        } = payload;
        const pageSize = customPageSize || 20;
        const afterCursor = page
          ? convertNumberToCursor((page - 1) * pageSize - 1)
          : '';
        const serviceArgs = [
          getPagedCustomerAdditionalDataFields,
          afterCursor,
          search,
          pageSize,
          queryDetail,
        ];
        function* onSuccess(data) {
          const additionalDataFieldKeys =
            data?.customerAdditionalDataFields?.edges?.map(
              (item) => item?.node?.fieldName,
            );
          const additionalDataFieldValues =
            data?.customerAdditionalDataFields?.edges?.map((item) => {
              return {
                ...item?.node,
                options: item?.node?.choices?.edges?.map((choiceItem) => {
                  return {
                    label: choiceItem?.node?.displayLabel,
                    value: choiceItem?.node?.fieldValue,
                  };
                }),
              };
            });
          yield put({
            type: 'updateAllCustomerAdditionalDataFields',
            payload: {
              additionalDataFieldNames: additionalDataFieldKeys,
              additionalDataFieldValues,
              page: page,
            },
          });
          actionAfterSuccess && actionAfterSuccess(additionalDataFieldKeys);
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    sendMessageToResetPassword: [
      function* ({ payload }, { select, put }) {
        const { ssoUid, messageTarget, afterAction } = payload;
        const serviceArgs = [sendMessageToResetPassword, ssoUid, messageTarget];
        function* onSuccess(data) {
          yield put({
            type: 'navBars/updateState',
            payload: {
              saveDiscardToastShowing: {
                value: true,
                type: 'Messgae has been sent',
              },
            },
          });
          afterAction && afterAction();
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    getAllCustomerProfileFields: [
      function* ({ payload }, { select, put }) {
        const serviceArgs = [getAllCustomerProfileFields];
        function* onSuccess(data) {
          const parsedData = data?.customerProfileFields?.edges?.map(
            (item) => ({
              ...item.node,
            }),
          );
          yield put({
            type: 'updateState',
            payload: {
              customerProfileFields: parsedData || [],
            },
          });
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    transferCustomer: [
      function* ({ payload }, { put }) {
        const serviceArgs = [transferCustomer, payload?.input];
        function* onSuccess(data) {
          yield put({
            type: 'navBars/updateState',
            payload: {
              saveDiscardToastShowing: {
                value: true,
                type: 'It may take few minutes to process the account merging.',
              },
            },
          });
          if (payload?.afterAction) {
            setTimeout(() => {
              payload.afterAction();
            }, 2000);
          }
        }

        function onFailed(data) {
          if (payload?.errorAction) {
            payload.errorAction();
          }
        }

        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed, onFailed);
      },
      { type: 'takeLatest' },
    ],
  },
};
